import { useState, useMemo, useCallback } from 'react';
import { Discount } from '../api/customer/types';

const MIN_AMOUNT = 2;
const NO_DISCOUNTS: Discount[] = [];
const roundAmount = (value: number) => Math.round(value * 100) / 100;

export type AmountType = {
    amount: number; // Исходная сумма без скидки
    amountDisplay: string; // Общая сумма для отображения
    hasDiscount: boolean;
    amountWithDiscount: number; // Общая сумма со скидкой
    discount?: number; // Скидка в процентах
    discountAmount?: number; // Размер скидки в денежном выражении
    discountDuration?: number; // Количество месяцев оплаты тарифа (для текста скидки)
    isValid: boolean;
};

type PaymentAmountParams = {
    monthlyPrice: number; // Стоимость месяца
    debt?: number; // Долг (если баланс отрицательный, то это Math.abs(balance))
    discountsForPeriods?: Discount[]; // Скидки для периодов из API
    /** Переопределение минимальной суммы (например, для статуса new — оплата за 3 месяца) */
    minPeriodForPayment?: number;
};

export const usePaymentAmount = (params: PaymentAmountParams) => {
    const { monthlyPrice = 0, debt = 0, discountsForPeriods = NO_DISCOUNTS, minPeriodForPayment } = params;

    // Сортируем скидки по месяцам по убыванию (от большего к меньшему)
    const sortedDiscounts = useMemo(
        () => [...discountsForPeriods].sort((a, b) => b.months - a.months),
        [discountsForPeriods],
    );

    const outstandingDebt = Math.max(0, debt);

    // Минимальное пополнение включает полное погашение задолженности.
    const minAmount = useMemo(() => {
        const minimumPrepayment = minPeriodForPayment ? minPeriodForPayment * monthlyPrice : MIN_AMOUNT;
        return roundAmount(outstandingDebt + minimumPrepayment);
    }, [outstandingDebt, minPeriodForPayment, monthlyPrice]);

    // Функция для определения скидки по количеству месяцев (возвращает процент и наличие)
    const getDiscountInfoByMonths = useCallback(
        (
            months: number,
        ): { hasDiscount: boolean; discount: number; percentOfAmount: number; monthsAtDiscount: number } => {
            for (const discountItem of sortedDiscounts) {
                if (months >= discountItem.months) {
                    return {
                        hasDiscount: discountItem.discount > 0,
                        discount: discountItem.discount,
                        percentOfAmount: discountItem.percentOfAmount,
                        monthsAtDiscount: discountItem.months,
                    };
                }
            }
            return {
                hasDiscount: false,
                discount: 0,
                percentOfAmount: 1,
                monthsAtDiscount: 0,
            };
        },
        [sortedDiscounts],
    );

    // Функция для определения скидки на основе суммы
    const getDiscountByAmount = (originalAmount: number): { discount: number; periodMonths: number } => {
        if (monthlyPrice <= 0 || !discountsForPeriods.length) return { discount: 0, periodMonths: 0 };

        for (const discountItem of sortedDiscounts) {
            const threshold =
                Math.floor(monthlyPrice * discountItem.percentOfAmount * discountItem.months * 10) / 10;
            if (Math.round(originalAmount * 100) >= Math.round(threshold * 100)) {
                return {
                    discount: discountItem.discount,
                    periodMonths: discountItem.months,
                };
            }
        }
        return { discount: 0, periodMonths: 0 };
    };

    const calculateAmount = (originalAmount: number): AmountType => {
        const prepayment = Math.max(0, originalAmount - outstandingDebt);
        const { discount, periodMonths } = getDiscountByAmount(prepayment);

        const hasDiscount = discount > 0;
        let discountAmount = undefined;
        if (hasDiscount) {
            discountAmount = prepayment * (discount / 100);
        }

        return {
            amount: originalAmount,
            amountDisplay: originalAmount !== 0 ? originalAmount.toString() : '',
            hasDiscount,
            amountWithDiscount: originalAmount - (discountAmount || 0),
            discount,
            discountAmount,
            discountDuration: periodMonths,
            isValid: originalAmount >= minAmount,
        };
    };

    // Пересчитываем валидацию и скидку при обновлении баланса клиента.
    const [originalAmount, setAmountState] = useState(minAmount);
    const amount = calculateAmount(originalAmount);

    // Функция для изменения суммы и опционально скидки
    const setAmount = useCallback((originalAmount: number) => {
        setAmountState(roundAmount(originalAmount));
    }, []);

    // Сумма к оплате за период с учётом скидки.
    // Единственный источник расчёта суммы по количеству месяцев — используется и при
    // инициализации экрана, и при смене периода в селекте
    const getAmountByMonths = useCallback(
        (months: number): number => {
            const { monthsAtDiscount, percentOfAmount } = getDiscountInfoByMonths(months);
            // Складываем месяцы со скидкой и месяцы, которые не попадут в скидку
            // Например оплата на 8 месяцев, 6 месяцев со скидкой 5% и 2 месяца без скидки
            return roundAmount(
                outstandingDebt +
                    (months - monthsAtDiscount) * monthlyPrice +
                    monthsAtDiscount * (monthlyPrice * percentOfAmount),
            );
        },
        [getDiscountInfoByMonths, outstandingDebt, monthlyPrice],
    );

    const setAmountByMonths = (months: number) => {
        setAmount(getAmountByMonths(months));
    };

    return {
        amount,
        setAmount,
        setAmountByMonths,
        getAmountByMonths,
        minAmount,
        getDiscountInfoByMonths,
    };
};
